import { forwardRef, type Ref } from "react"
import Link, {
  type LinkProps,
} from "@/v2-backoffice-client/components/link/Link"
import useSidebarContext from "@/v2-backoffice-client/components/sidebar/sidebar.context"
import SidebarItemContent from "@/v2-backoffice-client/components/sidebar/SidebarItemContent"
import SidebarItemTooltip from "@/v2-backoffice-client/components/sidebar/SidebarItemTooltip"
import {
  getSidebarItemClasses,
  type SidebarItemVisualProps,
} from "@/v2-backoffice-client/components/sidebar/sidebar.item.constants"

export type SidebarLinkProps = Omit<
  LinkProps,
  "variant" | "size" | "iconSrc" | "iconSize" | "iconClassName" | "iconStyle"
> &
  SidebarItemVisualProps

/**
 * A sidebar entry that navigates. It renders a real anchor with a real href,
 * so the destination is middle-clickable, openable in a new tab and
 * copyable - all the things a route reached through `onClick={() =>
 * navigate(...)}` silently takes away.
 */
function SidebarLink(props: SidebarLinkProps, ref: Ref<HTMLAnchorElement>) {
  const {
    className,
    isActive,
    isDisabled,
    isLoading,
    iconSrc,
    iconClassName,
    tooltipContent,
    tooltipDisabled = false,
    isChildrenHiddenOnMini,
    badge,
    children,
    ...linkProps
  } = props

  const { isMini } = useSidebarContext()

  return (
    <SidebarItemTooltip
      isEnabled={(isMini && !tooltipDisabled) || Boolean(tooltipContent)}
      content={tooltipContent || children}
    >
      <Link
        ref={ref}
        variant="unstyled"
        // Link's own iconSrc appends a trailing link/external glyph; the
        // sidebar's icon is a leading one, drawn by SidebarItemContent.
        iconSrc={null}
        aria-current={isActive ? "page" : undefined}
        aria-disabled={isDisabled || undefined}
        tabIndex={isDisabled ? -1 : undefined}
        className={getSidebarItemClasses({
          isMini,
          isActive,
          isDisabled,
          className,
        })}
        {...linkProps}
      >
        <SidebarItemContent
          isLoading={isLoading}
          isChildrenHiddenOnMini={isChildrenHiddenOnMini}
          iconSrc={iconSrc}
          iconClassName={iconClassName}
          badge={badge}
        >
          {children}
        </SidebarItemContent>
      </Link>
    </SidebarItemTooltip>
  )
}

export default forwardRef(SidebarLink)
